import React from 'react';
import { useSelector } from 'react-redux';

import Section from './section';
import LinkToOnboarding from './onboard-section-link';
import paymentAndSubscriptionPageMessages from '../payment-and-subscription/page/messages';
import Global from '../../../store/global-selectors';
import { Carriers } from '../../../constants/carriers';
import { useEnableAddOnCentral } from '../../../feature-flags/hooks/use-enable-add-on-central';
import { useBTEmailExperienceNew } from '../../../hooks/use-seller-setting';

import PrintingIcon from './images/printing-icon.svg';
import LabelsIcon from './images/labels-icon.svg';
import AddOnsIcon from './images/addons-icon.svg';
import AccountIcon from './images/account-icon.svg';
import ImportOrdersIcon from './images/import-orders-icon.svg';
import CustomerCommunicationIcon from './images/customer-communication-icon.svg';
import OnboardIcon from './images/onboarding-icon.svg';

import { useEnableHomePage } from '../../../feature-flags/hooks/use-enable-home-page';
import { useEnableHomePage2 } from '../../../feature-flags/hooks/use-enable-home-page2';
import { useEnableSupplyPage } from '../../../feature-flags/hooks/use-enable-supply-page';

import messages from './messages';
import { SuppliesCardContainer } from '../../home-page/version2/supplies-cards/supplies-card-container';
import { SUPPLIES_DISPLAY_LOCATION } from '../../home-page/version2/supplies-cards/supplies-page-constants';

import commonStyles from '../settings-page.module.less';
import style from './style.module.less';

const subsectionsMap = {
  importOrders: [
    {
      pageKey: 'stores',
      linkTitle: messages.storeSetup,
      blurb: messages.storeSetupBlurb,
    },
    {
      pageKey: 'automationRules',
      linkTitle: messages.automationRules,
      blurb: messages.automationRulesBlurb,
    },
  ],
  printing: [
    {
      pageKey: 'printing',
      linkTitle: messages.documentOptions,
      blurb: messages.documentOptionsBlurb,
    },
    {
      pageKey: 'connect',
      linkTitle: messages.connect,
      blurb: messages.connectBlurb,
    },
  ],
  labels: [
    {
      pageKey: 'carriers',
      linkTitle: messages.carriers,
      blurb: '',
    },
    {
      pageKey: 'warehouses',
      linkTitle: messages.warehouses,
      blurb: messages.warehousesBlurb,
    },
    {
      pageKey: 'packages',
      linkTitle: messages.packageTypesAndServices,
      blurb: messages.packageTypesAndServicesBlurb,
    },
  ],
  customer: [
    {
      pageKey: 'packingSlips',
      linkTitle: messages.packingSlips,
      blurb: messages.packingSlipsBlurb,
    },
    {
      pageKey: 'trackingPages',
      linkTitle: messages.brandedTracking,
      blurb: messages.brandedTrackingBlurb,
    },
  ],
  account: [
    {
      pageKey: 'users',
      linkTitle: messages.users,
      blurb: messages.usersBlurb,
    },
    {
      pageKey: 'paymentAndSubscription',
      linkTitle: paymentAndSubscriptionPageMessages.header,
      // TODO: SPD-11422 - this blurb needs an update
      blurb: messages.subscriptionBlurb,
    },
    {
      pageKey: 'myProfile',
      linkTitle: messages.changePassword,
    },
  ],
  onboard: [
    {
      pageKey: 'onboard',
      linkTitle: messages.onboardingGuide,
      blurb: messages.onboardingGuideDescription,
      customLink: <LinkToOnboarding />,
    },
  ],
  addOn: [
    {
      pageKey: 'addOnMarketplace',
      linkTitle: messages.addOnLinkText,
      blurb: messages.addOnDescription,
    },
  ],
};

export const MostPopular = () => {
  const enableHomePage = useEnableHomePage();
  const enableHomePage2 = useEnableHomePage2();
  const isHomePageEnabled = enableHomePage || enableHomePage2;
  const enableSupplyPage = useEnableSupplyPage();
  const carriers = useSelector(Global.selectReferenceDatagetSellerCarriers);
  const hasUSPSCarrier = carriers.some(({ carrierId }) => carrierId === Carriers.USPS.carrierId);
  const enableAddOnCentral = useEnableAddOnCentral();
  const enableNewEmailExperience = useBTEmailExperienceNew();

  const customerSections = React.useMemo(() => {
    if (!enableNewEmailExperience) {
      return [
        {
          pageKey: 'emailTemplates',
          linkTitle: messages.emailTemplates,
          blurb: messages.emailTemplatesBlurb,
        },
        ...subsectionsMap.customer,
      ];
    }

    return subsectionsMap.customer;
  }, [enableNewEmailExperience]);

  subsectionsMap.labels[0].blurb = hasUSPSCarrier ? messages.carriersBlurb : messages.carriersBlurbNonUSPS;

  return (
    <div className={commonStyles.content}>
      <div className={style.container}>
        <div className={style.row}>
          <Section
            icon={<img alt={messages.importOrders} src={ImportOrdersIcon} />}
            subsections={subsectionsMap.importOrders}
            title={messages.importOrders}
          />
          <Section
            icon={<img alt={messages.documentOptions} src={PrintingIcon} />}
            subsections={subsectionsMap.printing}
            title={messages.printing}
          />
          <Section icon={<img alt={messages.labels} src={LabelsIcon} />} subsections={subsectionsMap.labels} title={messages.labels} />
          <Section
            icon={<img alt={messages.customer} src={CustomerCommunicationIcon} />}
            subsections={customerSections}
            title={messages.customer}
          />
          <Section icon={<img alt={messages.account} src={AccountIcon} />} subsections={subsectionsMap.account} title={messages.account} />
          {!isHomePageEnabled && (
            <Section
              icon={<img alt={messages.onboardingGuide} src={OnboardIcon} />}
              subsections={subsectionsMap.onboard}
              title={messages.onboard}
            />
          )}
          {enableAddOnCentral && (
            <Section
              icon={<img alt={messages.addOnLinkText} src={AddOnsIcon} />}
              subsections={subsectionsMap.addOn}
              title={messages.addOnMarketplace}
            />
          )}
        </div>
        {enableSupplyPage && <SuppliesCardContainer page={SUPPLIES_DISPLAY_LOCATION.SETTINGS} />}
      </div>
    </div>
  );
};
